<template>
  <div class="mobile">
    <div class="mobile__header">
      <div class="my-auto">
        <AppIcon
          name="i-lucide-menu"
          data-testid="mobile-menu-open"
          class="cursor-pointer align-middle"
          size="2rem"
          @click="isOpen = true"
        />
      </div>
      <div
        :class="{ mobile__brand__kern: instanceVendor === 'kern' }"
        class="mobile__brand"
      >
        <router-link
          :to="HOME_ROUTE"
          data-testid="mobile-home-link-header"
          aria-label="Home"
          @click="isOpen = false"
        >
          <img v-if="logoPath" :src="logoPath" class="h-[3.15rem]" alt="Home" />
          <AppIcon
            v-else
            name="i-heroicons-arrow-path-20-solid"
            class="logo-spinner h-[3.15rem]"
            size="2rem"
          />
        </router-link>
      </div>
      <div class="my-auto ml-auto">
        <AskBrunonButton header />
      </div>
    </div>
    <USlideover
      v-model:open="isOpen"
      side="left"
      :title="$t('menu.navigation')"
      :description="$t('menu.mobile_menu')"
    >
      <template #content>
        <div class="mobile__header__slider">
          <div class="my-auto">
            <AppIcon
              name="i-lucide-x"
              data-testid="mobile-menu-close"
              class="cursor-pointer align-middle"
              size="2rem"
              @click="isOpen = false"
            />
          </div>
          <div
            :class="{ mobile__brand__kern: instanceVendor === 'kern' }"
            class="mobile__brand"
          >
            <router-link
              :to="HOME_ROUTE"
              data-testid="mobile-home-link-drawer"
              aria-label="Home"
              @click="isOpen = false"
            >
              <img
                v-if="logoPath"
                :src="logoPath"
                class="h-[3.15rem]"
                alt="Home"
              />
              <AppIcon
                v-else
                name="i-heroicons-arrow-path-20-solid"
                class="logo-spinner h-[3.15rem]"
                size="2rem"
              />
            </router-link>
          </div>
        </div>
        <div class="flex h-full flex-col justify-between bg-neutral-50 pb-10">
          <div class="flex flex-col gap-0">
            <UserAndLang :hovered="true" @link-clicked="isOpen = false" />
            <LinksMenu
              :hovered="true"
              :show-links="[
                '/translate-text',
                '/documents-viewer',
                '/language-correction',
                '/dictionary',
              ]"
              @link-clicked="isOpen = false"
            />
          </div>
          <LogoutButton :hovered="true" />
        </div>
      </template>
    </USlideover>
  </div>
</template>

<script>
import AskBrunonButton from '@/components/Utilities/AskBrunonButton.vue'
import LinksMenu from '@/components/Utilities/LinksMenu.vue'
import UserAndLang from '@/components/Utilities/UserAndLang.vue'
import LogoutButton from '@/components/Utilities/LogoutButton.vue'
import { HOME_ROUTE } from '~~/shared/routes'
export default {
  name: 'MobileMenu',
  components: {
    AskBrunonButton,
    LinksMenu,
    UserAndLang,
    LogoutButton,
  },
  props: {
    logoUrl: {
      type: String,
      default: null,
    },
  },
  emits: ['menuOpened', 'menuClosed'],
  setup() {
    const config = useRuntimeConfig()
    return { publicConfig: config.public }
  },
  data() {
    return {
      HOME_ROUTE,
      isOpen: false,
      logoPath: '',
      selectedLanguage: 'en-GB',
      instanceVendor: this.publicConfig?.instanceVendor || 'laniqo',
    }
  },

  watch: {
    isOpen(newValue, oldValue) {
      if (newValue) {
        this.$emit('menuOpened')
      } else if (oldValue) {
        this.$emit('menuClosed')
      }
    },
  },
  watch: {
    logoUrl(newUrl) {
      if (newUrl) {
        this.logoPath = newUrl
      }
    },
  },

  async created() {
    const savedLanguage = localStorage.getItem('selectedLanguage')
    if (savedLanguage !== null && savedLanguage !== 'undefined') {
      this.selectedLanguage = savedLanguage
      this.$i18n.locale = savedLanguage
    } else {
      localStorage.setItem('selectedLanguage', this.selectedLanguage)
    }
    const lastTargets = JSON.parse(
      localStorage.getItem('lastSelectedTargets') || '[]'
    )
    if (lastTargets.length === 0) {
      const initialTargets = [this.selectedLanguage]
      localStorage.setItem(
        'lastSelectedTargets',
        JSON.stringify(initialTargets)
      )
    }

    // Use prop logo if available, otherwise show spinner (don't load static logo)
    if (this.logoUrl) {
      this.logoPath = this.logoUrl
    }
  },
}
</script>

<style lang="scss" scoped>
.mobile {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-bottom: 4.5rem;
  width: 100%;
  overflow-y: hidden;
}

.mobile__brand__kern {
  margin-top: 0.25rem;
}

.mobile__header {
  display: flex;
  flex-direction: row;
  padding: 0 1.5rem;
  background-color: #fff;
  width: 100%;
  height: 3.1625rem;
  position: fixed;
  top: 0;
  z-index: 40;
  gap: 1rem;
  border-bottom: 1px solid var(--color-neutral-300);
}

.mobile__header__slider {
  display: flex;
  flex-direction: row;
  padding: 0 1.5rem;
  background-color: #fff;
  width: 100%;
  height: 3.15rem;
  gap: 1rem;
}

.logo-spinner {
  color: var(--color-primary-600);
  animation: spin 1s linear infinite;
  display: block;
  margin: 0 auto;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
